// Pure helpers for policyJson.conditions and .actions: tidy them for saving,
// check them with the engine's own rules (so most mistakes never reach the
// server), and put them into words for the editor, the summaries and the
// policy lists. No React here.
import {
  AUTH_STRENGTH_OPTIONS,
  COUNTRY_INTERNAL,
  HIDDEN_CONDITIONS,
  SET_OUTSIDE,
  DAY_OPTIONS,
  DEVICE_TRUST_OPTIONS,
  DIRECTORY_OPTIONS,
  ENGINE_DAY_ORDER,
  PLATFORM_OPTIONS,
  PRIVILEGE_OPTIONS,
  RISK_OPTIONS,
  SENSITIVITY_OPTIONS,
  offeredConditions,
} from "./signals";
import COUNTRIES from "../../pages/enduser/policy/countries";

// ISO code -> name ("IN" -> "India"), for the Country condition.
export const COUNTRY_NAMES = Object.fromEntries(COUNTRIES.map((c) => [c.code, c.name]));
export const COUNTRY_OPTIONS = [
  { value: COUNTRY_INTERNAL, label: "Internal network (office, VPN)" },
  ...COUNTRIES.map((c) => ({ value: c.code, label: `${c.name} (${c.code})` })),
];

// Section key -> list keys, in the engine's order (validate.go checks them in
// this order and reports the first problem).
export const CONDITION_LISTS = {
  identity: ["directories", "excludeUsers", "excludeGroups"],
  authStrength: ["levels"],
  device: ["trust", "platforms"],
  resource: ["sensitivity"],
  privilege: ["levels"],
  time: ["days"],
  network: ["include", "exclude"],
  risk: ["levels"],
  // Not offered by the console; a stored one is kept as it is.
  country: ["include", "exclude"],
};
export const CONDITION_KEYS = Object.keys(CONDITION_LISTS);

const cleanList = (list) => {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const out = [];
  list.forEach((v) => {
    const s = String(v ?? "").trim();
    if (s && !seen.has(s.toLowerCase())) {
      seen.add(s.toLowerCase());
      out.push(s);
    }
  });
  return out;
};

/* ------------------------------------------------------------ normalise */

// The conditions as they should be saved: lists trimmed and de-duplicated,
// empty lists and empty sections dropped (empty means "any"), undefined when
// nothing is left so the key is omitted altogether.
//
// keepUnknown: keep sections this console doesn't know (a newer engine's, or
// one set by SQL) as they are, so saving never drops them (spec 7.1).
export function normaliseConditions(c, { keepUnknown = false } = {}) {
  if (!c || typeof c !== "object") return undefined;
  const out = {};
  if (keepUnknown) {
    Object.keys(c).forEach((key) => {
      if (!CONDITION_LISTS[key] && c[key] !== undefined && c[key] !== null) out[key] = c[key];
    });
  }
  CONDITION_KEYS.forEach((key) => {
    const sec = c[key];
    if (!sec || typeof sec !== "object") return;
    const next = {};
    CONDITION_LISTS[key].forEach((k) => {
      let raw = sec[k];
      if (key === "network" && Array.isArray(raw)) raw = raw.map(normaliseNetworkEntry);
      if (key === "country" && Array.isArray(raw)) raw = raw.map(normaliseCountry);
      const list = cleanList(raw);
      if (list.length) next[k] = list;
    });
    if (key === "time") {
      const start = String(sec.start ?? "").trim();
      const end = String(sec.end ?? "").trim();
      const from = String(sec.from ?? "").trim();
      const to = String(sec.to ?? "").trim();
      if (start) next.start = start;
      if (end) next.end = end;
      // One-time window: not offered by the console; a stored one is kept.
      if (from) next.from = from;
      if (to) next.to = to;
      // A window with no days and no hours always holds: drop it, timezone and
      // all, rather than save a condition that does nothing.
      if (!next.days && !start && !end && !from && !to) return;
      const tz = String(sec.timezone ?? "").trim();
      if (tz) next.timezone = tz;
      if (sec.outside) next.outside = true;
    }
    if (Object.keys(next).length) out[key] = next;
  });
  return Object.keys(out).length ? out : undefined;
}

const nonNegInt = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : 0;
};

// The actions as they should be saved: approval only when required, session
// only when it does something; undefined when neither is left. With
// policyFlow "block" approval is dropped: the form shows it as not used there.
export function normaliseActions(a, { policyFlow } = {}) {
  if (!a || typeof a !== "object") return undefined;
  const out = {};
  const ap = a.approval;
  if (ap && ap.required && policyFlow !== "block") {
    const approval = { required: true };
    const approvers = cleanList(ap.approvers);
    const groups = cleanList(ap.approverGroups);
    if (approvers.length) approval.approvers = approvers;
    if (groups.length) approval.approverGroups = groups;
    if (ap.requireJustification) approval.requireJustification = true;
    if (ap.requireTicket) approval.requireTicket = true;
    const mins = nonNegInt(ap.validMinutes);
    if (mins) approval.validMinutes = mins;
    out.approval = approval;
  }
  const s = a.session;
  if (s) {
    const session = {};
    if (s.record) session.record = true;
    [["maxDurationMinutes"], ["idleTimeoutMinutes"], ["reauthIntervalMinutes"]].forEach(([k]) => {
      const n = nonNegInt(s[k]);
      if (n) session[k] = n;
    });
    if (Object.keys(session).length) out.session = session;
  }
  return Object.keys(out).length ? out : undefined;
}

/* --------------------------------------------------------------- checks */

// Go's net.ParseIP: dotted IPv4 without leading zeros, or IPv6 (optionally
// ending in dotted IPv4), no zone.
const isIPv4 = (s) => {
  const parts = s.split(".");
  if (parts.length !== 4) return false;
  return parts.every((p) => /^\d{1,3}$/.test(p) && (p === "0" || p[0] !== "0") && Number(p) <= 255);
};

const isIPv6 = (s) => {
  if (!s.includes(":")) return false;
  const halves = s.split("::");
  if (halves.length > 2) return false;
  const groupsOf = (h) => (h === "" ? [] : h.split(":"));
  const head = groupsOf(halves[0]);
  const tail = halves.length === 2 ? groupsOf(halves[1]) : [];
  const all = [...head, ...tail];
  let count = 0;
  for (let i = 0; i < all.length; i += 1) {
    const g = all[i];
    const last = i === all.length - 1;
    if (last && g.includes(".")) {
      if (!isIPv4(g)) return false;
      count += 2;
    } else if (/^[0-9a-fA-F]{1,4}$/.test(g)) {
      count += 1;
    } else {
      return false;
    }
  }
  // "::" must stand for at least one group of zeros; without it, exactly 8.
  return halves.length === 2 ? count < 8 : count === 8;
};

// An IP address or CIDR, as the engine accepts it (network.include/exclude).
export function isValidIpOrCidr(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return false;
  const slash = s.indexOf("/");
  if (slash === -1) return isIPv4(s) || isIPv6(s);
  const addr = s.slice(0, slash);
  const prefix = s.slice(slash + 1);
  if (!/^\d{1,3}$/.test(prefix)) return false;
  const n = Number(prefix);
  if (isIPv4(addr)) return n <= 32;
  if (isIPv6(addr)) return n <= 128;
  return false;
}

// An entry as it is stored: trimmed. (IP ranges aren't offered: the engine
// takes IPs and CIDRs only.)
export const normaliseNetworkEntry = (raw) => String(raw ?? "").trim();

// One Network entry: an IP address or a CIDR.
export const isValidNetworkEntry = (raw) => isValidIpOrCidr(raw);

// Go's time.Parse("15:04"): hour of one or two digits, minutes of two.
const parseClock = (raw) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(raw ?? "").trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
};

export const isValidTimezone = (tz) => {
  if (!tz) return true; // empty is UTC
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz }).format(0);
    return true;
  } catch {
    return false;
  }
};

const q = (v) => JSON.stringify(String(v)); // Go's %q for plain strings
const values = (opts) => opts.map((o) => o.value);
const VOCAB = {
  "identity.directories": values(DIRECTORY_OPTIONS),
  "authStrength.levels": values(AUTH_STRENGTH_OPTIONS),
  "device.trust": values(DEVICE_TRUST_OPTIONS),
  "device.platforms": values(PLATFORM_OPTIONS),
  "resource.sensitivity": values(SENSITIVITY_OPTIONS),
  "privilege.levels": values(PRIVILEGE_OPTIONS),
  "time.days": ENGINE_DAY_ORDER,
  "risk.levels": values(RISK_OPTIONS),
};
const oneOf = (field, list) => {
  const allowed = VOCAB[field];
  const bad = (list || []).find((v) => !allowed.includes(String(v).toLowerCase()));
  return bad === undefined ? null : `${field}: ${q(bad)} is not one of ${allowed.join(", ")}`;
};

// A one-time bound as the editor writes it: "2026-11-03T09:00" (local time
// in the condition's timezone, no offset).
const LOCAL_DATETIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;
export const parseLocalDateTime = (raw) => {
  const m = LOCAL_DATETIME.exec(String(raw ?? "").trim());
  if (!m) return null;
  const [y, mo, d, h, mi] = m.slice(1).map(Number);
  if (mo < 1 || mo > 12 || d < 1 || d > 31 || h > 23 || mi > 59) return null;
  return Date.UTC(y, mo - 1, d, h, mi);
};

const timeError = (t) => {
  if (t.timezone && !isValidTimezone(t.timezone)) return `time.timezone: ${q(t.timezone)} is not a known timezone`;
  if (t.from || t.to) {
    if (!t.from || !t.to) return "time: from and to must both be set";
    const f = parseLocalDateTime(t.from);
    if (f === null) return `time.from: ${q(t.from)} is not a date and time`;
    const e = parseLocalDateTime(t.to);
    if (e === null) return `time.to: ${q(t.to)} is not a date and time`;
    if (e <= f) return "time: to must be after from";
    return null;
  }
  const days = oneOf("time.days", t.days);
  if (days) return days;
  const start = t.start || "";
  const end = t.end || "";
  if (!start !== !end) return "time: start and end must both be set, or neither";
  if (!start) return null;
  const s = parseClock(start);
  if (s === null) return `time.start: ${q(start)} is not HH:MM`;
  const e = parseClock(end);
  if (e === null) return `time.end: ${q(end)} is not HH:MM`;
  if (s === e) return `time: start and end are both ${start}, which is an empty window`;
  return null;
};

// Every problem in the conditions, keyed by section ("time", "network", ...),
// in the engine's own words. {} when there is none. Checked on the normalised
// value, which is what is sent.
export function validateConditions(conditions) {
  const c = normaliseConditions(conditions);
  const errors = {};
  if (!c) return errors;
  const set = (key, msg) => {
    if (msg && !errors[key]) errors[key] = msg;
  };
  if (c.identity) set("identity", oneOf("identity.directories", c.identity.directories));
  if (c.authStrength) set("authStrength", oneOf("authStrength.levels", c.authStrength.levels));
  if (c.device) set("device", oneOf("device.trust", c.device.trust) || oneOf("device.platforms", c.device.platforms));
  if (c.resource) set("resource", oneOf("resource.sensitivity", c.resource.sensitivity));
  if (c.privilege) set("privilege", oneOf("privilege.levels", c.privilege.levels));
  if (c.time) set("time", timeError(c.time));
  if (c.network) {
    const bad = [...(c.network.include || []), ...(c.network.exclude || [])].find((e) => !isValidNetworkEntry(e));
    if (bad !== undefined) set("network", `network: ${q(bad)} is not an IP address or CIDR`);
  }
  if (c.risk) set("risk", oneOf("risk.levels", c.risk.levels));
  if (c.country) {
    const bad = [...(c.country.include || []), ...(c.country.exclude || [])].find((v) => !/^[A-Z]{2}$/.test(v) && v !== COUNTRY_INTERNAL);
    if (bad !== undefined) set("country", `country: ${q(bad)} is not a country code`);
  }
  return errors;
}

// Every problem in the actions, keyed "approval" / "session", in the engine's
// words (dto/actions.go). Checked on the raw value so negative numbers are
// caught before normalising would hide them. Approval is skipped for
// policyFlow "block", where normaliseActions drops it.
export function validateActions(actions, { policyFlow } = {}) {
  const errors = {};
  if (!actions || typeof actions !== "object") return errors;
  const ap = policyFlow === "block" ? null : actions.approval;
  if (ap) {
    if (ap.required && !cleanList(ap.approvers).length && !cleanList(ap.approverGroups).length) {
      errors.approval = "actions.approval: required, but no approvers or approver groups are named";
    } else if (Number(ap.validMinutes) < 0) {
      errors.approval = "actions.approval.validMinutes: must not be negative";
    }
  }
  const s = actions.session;
  if (s) {
    const max = Number(s.maxDurationMinutes) || 0;
    const idle = Number(s.idleTimeoutMinutes) || 0;
    const reauth = Number(s.reauthIntervalMinutes) || 0;
    if (max < 0 || idle < 0 || reauth < 0) {
      errors.session = "actions.session: durations must not be negative";
    } else if (max > 0 && idle > max) {
      errors.session = "actions.session: idle timeout is longer than the maximum session";
    }
  }
  return errors;
}

// The first message of an errors object (or null), in the engine's order.
export const firstError = (errors) => {
  const order = [...CONDITION_KEYS, "approval", "session"];
  const key = order.find((k) => errors?.[k]) || Object.keys(errors || {})[0];
  return key ? errors[key] : null;
};

/* ---------------------------------------------------------------- words */

const labelOf = (opts, v) => opts.find((o) => o.value === String(v).toLowerCase())?.label || v;
const labels = (opts, list) => (list || []).map((v) => labelOf(opts, v));
const orList = (items) =>
  items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} or ${items[items.length - 1]}`;
const andList = (items) =>
  items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
const lowerList = (opts, list) => labels(opts, list).map((s) => (s === "MFA" || s === "AD" ? s : s.toLowerCase()));

/* --------------------------------------------------------------- errors */

const INVALID_PREFIX = /^invalid policy:\s*/i;

// The reason from the service's 400 for an invalid policy, or null when err is
// anything else. Create answers { code: 400, status: "failed", message:
// "Invalid policy: <reason>" }, update answers { error: "Invalid policy:
// <reason>" }. Takes an axios error, a fetch-style { status, body }, or the
// body itself. Returns "<reason>" without the "Invalid policy: " prefix.
export function policyErrorMessage(err) {
  if (!err) return null;
  const body = err.response?.data ?? err.data ?? err.body ?? err;
  // A bare body has status "failed" and the number in code.
  const status = err.response?.status ?? (typeof err.status === "number" ? err.status : undefined) ?? body?.code;
  if (Number(status) !== 400) return null;
  const parsed = typeof body === "string" ? safeJson(body) : body;
  const text = [parsed?.message, parsed?.error].find((t) => typeof t === "string" && INVALID_PREFIX.test(t));
  return text ? text.replace(INVALID_PREFIX, "").trim() || null : null;
}

function safeJson(s) {
  try {
    return JSON.parse(s);
  } catch {
    return { message: s };
  }
}

/* ------------------------------------------------------------ time zones */

// The timezone's short name ("IST", "CET", "UTC") where Intl has one, else the
// IANA name. Several English locales are tried: en-US says "GMT+5:30" for
// India, en-IN says "IST".
export function tzAbbreviation(tz) {
  const zone = tz || "UTC";
  if (/^(utc|etc\/utc|gmt)$/i.test(zone)) return "UTC";
  const locales = [typeof navigator !== "undefined" ? navigator.language : null, "en-US", "en-GB", "en-IN", "en-AU"].filter(Boolean);
  for (const loc of locales) {
    try {
      const part = new Intl.DateTimeFormat(loc, { timeZone: zone, timeZoneName: "short" })
        .formatToParts(new Date())
        .find((x) => x.type === "timeZoneName")?.value;
      if (part && /^[A-Z]{2,5}$/.test(part)) return part;
    } catch {
      return zone;
    }
  }
  return zone;
}

// Old IANA names some browsers still report, mapped to the current ones
// (spec 5.4: Asia/Calcutta -> Asia/Kolkata). Only applied when the browser
// knows the new name.
const TZ_ALIASES = {
  "Asia/Calcutta": "Asia/Kolkata",
  "Asia/Saigon": "Asia/Ho_Chi_Minh",
  "Asia/Katmandu": "Asia/Kathmandu",
  "Asia/Rangoon": "Asia/Yangon",
  "Europe/Kiev": "Europe/Kyiv",
  "Etc/UTC": "UTC",
  "Etc/GMT": "UTC",
  GMT: "UTC",
};

export function canonicalTimezone(tz) {
  const z = String(tz || "").trim();
  const next = TZ_ALIASES[z];
  return next && isValidTimezone(next) ? next : z;
}

// The browser's own zone, canonical. UTC when it can't tell.
export function detectTimezone() {
  try {
    return canonicalTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC") || "UTC";
  } catch {
    return "UTC";
  }
}

// "Asia/Kolkata (IST)"; "UTC" for UTC; the bare name when there's no short one.
export function timezoneLabel(tz) {
  const zone = canonicalTimezone(tz) || "UTC";
  if (zone === "UTC") return "UTC";
  const abbr = tzAbbreviation(zone);
  return abbr && abbr !== zone ? `${zone} (${abbr})` : zone;
}

let tzOptionsCache = null;
// Every IANA zone the browser knows, canonical and de-duplicated, UTC first:
// [{ value, label }]. `extra` (a stored zone) is added when missing.
export function timezoneOptions(extra) {
  if (!tzOptionsCache) {
    let list = [];
    try {
      list = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];
    } catch {
      list = [];
    }
    const seen = new Set(["UTC"]);
    const zones = ["UTC"];
    [...list, detectTimezone()].map(canonicalTimezone).forEach((z) => {
      if (z && !seen.has(z)) {
        seen.add(z);
        zones.push(z);
      }
    });
    tzOptionsCache = zones.map((z) => ({ value: z, label: timezoneLabel(z) }));
  }
  const e = canonicalTimezone(extra);
  if (e && !tzOptionsCache.some((o) => o.value === e)) return [{ value: e, label: timezoneLabel(e) }, ...tzOptionsCache];
  return tzOptionsCache;
}

/* ------------------------------------------------------- friendly errors */
// Admins never see the engine's or the API's own text. These map it to one
// plain sentence; anything unrecognised gets the generic line and the caller
// logs the raw text.

export const GENERIC_SAVE_ERROR = "Couldn't save the policy. Try again, or contact support.";
// Update answered 404, or 500 "only policies with 'active' status can be
// updated": the policy was deleted or replaced since the page opened.
export const GONE_SAVE_ERROR = "This policy was deleted or changed since you opened it. Go back to the list and open it again.";
export const FRIENDLY_ERRORS = {
  approvers: "Name at least one approver.",
  clock: "Enter a time like 09:00.",
  network: "Enter an IP address or a CIDR like 10.0.0.0/8.",
  idle: "Idle timeout must be shorter than the maximum length.",
  minutes: "Enter 0 or more minutes.",
};

// A server reason (policyErrorMessage's result, or any text) ->
// { message, section, condition, known }. section is "approval", "session",
// "conditions" or null; condition names the condition for "conditions".
export function friendlyPolicyError(reason) {
  const r = String(reason ?? "").replace(INVALID_PREFIX, "").trim();
  if (/^actions\.approval\b/.test(r) && /no approvers/.test(r)) {
    return { message: FRIENDLY_ERRORS.approvers, section: "approval", condition: null, known: true };
  }
  if (/^time\.(start|end)\b/.test(r) && /HH:MM/.test(r)) {
    return { message: FRIENDLY_ERRORS.clock, section: "conditions", condition: "time", known: true };
  }
  if (/^network\b/.test(r) && /is not an IP address or CIDR/.test(r)) {
    return { message: FRIENDLY_ERRORS.network, section: "conditions", condition: "network", known: true };
  }
  if (/^actions\.session\b/.test(r) && /idle timeout is longer than the maximum/.test(r)) {
    return { message: FRIENDLY_ERRORS.idle, section: "session", condition: null, known: true };
  }
  if (/only policies with 'active' status can be updated|policy does not exist|not found/i.test(r)) {
    return { message: GONE_SAVE_ERROR, section: null, condition: null, known: true };
  }
  return { message: GENERIC_SAVE_ERROR, section: null, condition: null, known: false };
}

// One of validateConditions' messages for `key`, in plain words.
export function friendlyConditionError(key, msg) {
  const m = String(msg || "");
  if (!m) return null;
  if (key === "time") {
    if (/HH:MM/.test(m)) return FRIENDLY_ERRORS.clock;
    if (/start and end must both be set/.test(m)) return "Enter both a From and a To time.";
    if (/empty window/.test(m)) return "From and To can't be the same time.";
    if (/timezone/.test(m)) return "Pick a time zone from the list.";
    return "Pick days from the list.";
  }
  if (key === "network") return FRIENDLY_ERRORS.network;
  return "Pick from the list.";
}

// validateActions' messages, in plain words: { approval?, session? }.
export function friendlyActionErrors(errors) {
  const out = {};
  if (errors?.approval) out.approval = /no approvers/.test(errors.approval) ? FRIENDLY_ERRORS.approvers : FRIENDLY_ERRORS.minutes;
  if (errors?.session) out.session = /idle timeout/.test(errors.session) ? FRIENDLY_ERRORS.idle : FRIENDLY_ERRORS.minutes;
  return out;
}

// A stored one-time window (time.from / time.to): not offered by the console.
const isOneTime = (t) => !!(t && (t.from || t.to));

// The condition keys the editor lets an admin edit on `product` (all offered
// ones when no product is given). Anything else stored is read-only.
const editableKeys = (product) => offeredConditions(product).map((c) => c.key);
const isEditable = (c, key, product) => editableKeys(product).includes(key) && !(key === "time" && isOneTime(c.time));

// Everything wrong with the editable conditions as the editor holds them,
// keyed by condition, in plain words (spec 3): Network needs at least one
// address, IPs and CIDRs only; Time needs From and To. Read-only conditions
// are not checked: they can only be removed.
export function conditionProblems(conditions, { product } = {}) {
  const c = conditions && typeof conditions === "object" ? conditions : {};
  const out = {};
  const editable = (key) => c[key] && isEditable(c, key, product);
  if (editable("network") && !(c.network.include || []).length && !(c.network.exclude || []).length) {
    out.network = "Add at least one IP address or CIDR.";
  }
  if (editable("privilege") && !(c.privilege.levels || []).length) out.privilege = "Pick at least one level.";
  if (editable("risk") && !(c.risk.levels || []).length) out.risk = "Pick at least one level.";
  if (editable("country") && !(c.country.include || []).length && !(c.country.exclude || []).length) {
    out.country = "Pick at least one country, or Internal network.";
  }
  if (editable("time") && (!String(c.time.start || "").trim() || !String(c.time.end || "").trim())) {
    out.time = "Enter both a From and a To time.";
  }
  const engine = validateConditions(Object.fromEntries(Object.keys(c).filter(editable).map((k) => [k, c[k]])));
  Object.keys(engine).forEach((k) => {
    if (!out[k]) out[k] = friendlyConditionError(k, engine[k]);
  });
  return out;
}

/* ------------------------------------------------------- condition rows */

// "Mon–Fri", "Mon–Wed, Fri", "Sat, Sun", "Every day".
export function dayRunsLabel(days) {
  const order = DAY_OPTIONS.map((d) => d.value);
  const set = new Set((days || []).map((d) => String(d).toLowerCase()));
  const picked = order.filter((d) => set.has(d));
  if (!picked.length || picked.length === 7) return "Every day";
  const runs = [];
  let run = [];
  order.forEach((d) => {
    if (set.has(d)) run.push(d);
    else if (run.length) {
      runs.push(run);
      run = [];
    }
  });
  if (run.length) runs.push(run);
  const name = (d) => labelOf(DAY_OPTIONS, d);
  return runs
    .map((r) => (r.length >= 3 ? `${name(r[0])}–${name(r[r.length - 1])}` : r.map(name).join(", ")))
    .join(", ");
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// "2026-11-03T09:00" -> "3 Nov 09:00" (the year only when it isn't this one).
export function formatLocalDateTime(raw, { now = new Date() } = {}) {
  const t = parseLocalDateTime(raw);
  if (t === null) return String(raw || "");
  const d = new Date(t);
  const year = d.getUTCFullYear() !== now.getFullYear() ? ` ${d.getUTCFullYear()}` : "";
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}${year} ${hh}:${mm}`;
}

const tzName = (t) => canonicalTimezone(t?.timezone) || "UTC";

// "Mon–Fri 09:00–18:00 (Asia/Kolkata)" · "Outside Mon–Fri 09:00–18:00 (Asia/Kolkata)".
// A stored one-time window: "3 Nov 09:00 to 7 Nov 18:00 (Asia/Kolkata)".
export function timeSummary(t) {
  if (!t) return "";
  if (isOneTime(t)) return `${formatLocalDateTime(t.from)} to ${formatLocalDateTime(t.to)} (${tzName(t)})`;
  const win = t.start && t.end ? `${t.start}–${t.end}` : "";
  const text = `${[dayRunsLabel(t.days), win].filter(Boolean).join(" ")} (${tzName(t)})`;
  return t.outside ? `Outside ${text}` : text;
}

// "Only from 10.0.0.0/8, except 10.99.0.0/16" · "Never from 10.99.0.0/16".
export function networkSummary(n) {
  const inc = cleanList((n?.include || []).map(normaliseNetworkEntry));
  const exc = cleanList((n?.exclude || []).map(normaliseNetworkEntry));
  if (inc.length && exc.length) return `Only from ${orList(inc)}, except ${andList(exc)}`;
  if (inc.length) return `Only from ${orList(inc)}`;
  if (exc.length) return `Never from ${orList(exc)}`;
  return "";
}

const capital = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// "IN" / "internal" -> stored form: upper-case codes, "internal" as is.
export function normaliseCountry(v) {
  const t = String(v ?? "").trim();
  return t.toLowerCase() === COUNTRY_INTERNAL ? COUNTRY_INTERNAL : t.toUpperCase();
}
const countryName = (code) => (code === COUNTRY_INTERNAL ? "internal network" : COUNTRY_NAMES[code] || code);

// "Only from India or US" · "Never from internal network" · both.
export function countrySummary(cn) {
  const inc = cleanList((cn?.include || []).map(normaliseCountry)).map(countryName);
  const exc = cleanList((cn?.exclude || []).map(normaliseCountry)).map(countryName);
  if (inc.length && exc.length) return `Only from ${orList(inc)}, except ${andList(exc)}`;
  if (inc.length) return `Only from ${orList(inc)}`;
  if (exc.length) return `Not from ${orList(exc)}`;
  return "";
}

// "High risk only" · "Medium or high risk only".
export function riskSummary(r) {
  const lv = lowerList(RISK_OPTIONS, RISK_OPTIONS.map((o) => o.value).filter((v) => (r?.levels || []).map((x) => String(x).toLowerCase()).includes(v)));
  return lv.length ? capital(`${orList(lv)} risk only`) : "";
}

// "Admin sign-ins only" · "Elevated or admin sessions only".
export function privilegeSummary(p, subject = "sign-in") {
  const lv = PRIVILEGE_OPTIONS.filter((o) => (p?.levels || []).map((x) => String(x).toLowerCase()).includes(o.value));
  if (!lv.length) return "";
  return capital(`${orList(lv.map((o) => o.label.toLowerCase()))} ${subject}s only`);
}

// Read-only values of a condition the console doesn't offer.
const hiddenValues = (c, key) => {
  if (key === "authStrength") return lowerList(AUTH_STRENGTH_OPTIONS, c.authStrength?.levels);
  if (key === "device") {
    return [...lowerList(DEVICE_TRUST_OPTIONS, c.device?.trust), ...labels(PLATFORM_OPTIONS, c.device?.platforms)];
  }
  if (key === "resource") return lowerList(SENSITIVITY_OPTIONS, c.resource?.sensitivity);
  if (key === "identity.directories") return labels(DIRECTORY_OPTIONS, c.identity?.directories);
  return [];
};

const OFFERED_LABELS = { time: "Time", network: "Network", privilege: "Privilege", country: "Country", risk: "Risk" };
const offeredSummary = (c, key, subject) => {
  if (key === "time") return timeSummary(c.time);
  if (key === "network") return networkSummary(c.network);
  if (key === "country") return countrySummary(c.country);
  if (key === "risk") return riskSummary(c.risk);
  return privilegeSummary(c.privilege, subject);
};

/**
 * The Conditions block's rows: the editable ones in menu order, then the
 * read-only ones (spec 3: stored but not offered, shown with remove only):
 * [{ key, label, summary, hidden }]. Exclusions (identity.exclude*) are
 * Except, not a row. product: which conditions are editable (Privilege isn't
 * on Database; a one-time Time window isn't anywhere).
 */
export function conditionRows(conditions, { subject = "sign-in", product } = {}) {
  const c = conditions && typeof conditions === "object" ? conditions : {};
  const rows = [];
  const readOnly = [];
  Object.keys(OFFERED_LABELS).forEach((key) => {
    if (!c[key]) return;
    const label = OFFERED_LABELS[key];
    const summary = offeredSummary(c, key, subject);
    if (isEditable(c, key, product)) rows.push({ key, label, summary, hidden: false });
    else readOnly.push({ key, label, summary: `${label}: ${summary || "set"}, ${SET_OUTSIDE}`, hidden: true });
  });
  rows.push(...readOnly);
  HIDDEN_CONDITIONS.forEach(({ key, label }) => {
    const vals = hiddenValues(c, key);
    if (vals.length) rows.push({ key, label, summary: `${label}: ${vals.join(", ")}, ${SET_OUTSIDE}`, hidden: true });
  });
  return rows;
}

// conditions without `key` ("time", ..., or "identity.directories"); undefined
// when nothing is left.
export function removeCondition(conditions, key) {
  const next = { ...(conditions || {}) };
  if (key === "identity.directories") {
    if (next.identity) {
      const { directories, ...rest } = next.identity;
      if (Object.keys(rest).length) next.identity = rest;
      else delete next.identity;
    }
  } else {
    delete next[key];
  }
  return Object.keys(next).length ? next : undefined;
}

/* -------------------------------------------------------------- sentence */

// Clauses for the one-line policy summary (spec 5), lower case:
// ["outside Mon–Fri 09:00–18:00 (Asia/Kolkata)", "only from 10.0.0.0/8 but not 10.99.0.0/16", "admin sign-ins only"].
export function conditionClauses(conditions, { subject = "sign-in", product } = {}) {
  const c = conditions && typeof conditions === "object" ? conditions : {};
  const out = [];
  if (c.time && isEditable(c, "time", product)) {
    const t = timeSummary(c.time);
    if (t) out.push(c.time.outside ? `o${t.slice(1)}` : `during ${t}`);
  }
  if (c.network) {
    const inc = cleanList(c.network.include);
    const exc = cleanList(c.network.exclude);
    if (inc.length) out.push(`only from ${orList(inc)}${exc.length ? ` but not ${orList(exc)}` : ""}`);
    else if (exc.length) out.push(`never from ${orList(exc)}`);
  }
  if (c.privilege && isEditable(c, "privilege", product)) {
    const p = privilegeSummary(c.privilege, subject);
    if (p) out.push(p.toLowerCase());
  }
  if (c.country && isEditable(c, "country", product)) {
    const t = countrySummary(c.country);
    if (t) out.push(t.charAt(0).toLowerCase() + t.slice(1));
  }
  if (c.risk && isEditable(c, "risk", product)) {
    const t = riskSummary(c.risk);
    if (t) out.push(t.charAt(0).toLowerCase() + t.slice(1));
  }
  return out;
}

/* ---------------------------------------------------------------- lists */

/**
 * Badges for a policy's name cell: Fallback, Conditions (one summary per line
 * in the tooltip), and on SSH Approval and Recorded.
 * [{ key, label, tooltip, lines? }]
 * options.approval / options.session: the product has them (SSH).
 */
export function listBadges(policyJson, { approval = false, session = false, subject = "sign-in", product } = {}) {
  const pj = policyJson || {};
  const out = [];
  if (pj.fallback) out.push({ key: "fallback", label: "Fallback", tooltip: "Only applies when no other policy matches." });
  const rows = conditionRows(splitIdentity(pj.conditions), { subject, product });
  if (rows.length) {
    const lines = rows.map((r) => (r.hidden ? r.summary : `${r.label}: ${r.summary}`));
    out.push({ key: "conditions", label: "Conditions", tooltip: lines.join("\n"), lines });
  }
  const a = normaliseActions(pj.actions, { policyFlow: pj.policyFlow });
  if (approval && a?.approval) {
    const who = [...(a.approval.approvers || []), ...(a.approval.approverGroups || [])];
    out.push({ key: "approval", label: "Approval", tooltip: `Approval from ${who.length ? orList(who) : "nobody yet"}.` });
  }
  if (session && a?.session?.record) out.push({ key: "recorded", label: "Recorded", tooltip: "Sessions are recorded." });
  return out;
}

// conditions without identity.excludeUsers / excludeGroups (those are Except).
function splitIdentity(conditions) {
  if (!conditions || typeof conditions !== "object") return {};
  const next = { ...conditions };
  if (next.identity && typeof next.identity === "object") {
    const { excludeUsers, excludeGroups, ...rest } = next.identity;
    if (Object.keys(rest).length) next.identity = rest;
    else delete next.identity;
  }
  return next;
}
